import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Tab, Tabs, useMediaQuery } from '@mui/material';
import { getRawDataAdapter } from '../manufacturers/registry';
import { RawDataJsonView } from './RawDataJsonView';
import { RAW_JSON_TAB } from '../constants';

/** Same breakpoint as the SCSS: below it the tab list goes back on top, scrollable. */
const NARROW_QUERY = '(max-width: 600px)';

/** `window.matchMedia` can be missing or stubbed (jsdom); fall back to the wide layout. */
const NO_MATCH = { matches: false, addListener: () => {}, removeListener: () => {} };
const safeMatchMedia = (query) => (typeof window !== 'undefined' && window.matchMedia?.(query)) || NO_MATCH;

const tabA11y = (value) => ({ id: `oem-tab-${value}`, 'aria-controls': `oem-tabpanel-${value}` });

/**
 * Manufacturer-dispatched body: the adapter's tabs — plus the JSON view when
 * `showRawJson` — in a vertical tab list on the left, with the active panel
 * scrolling inside a fixed-height area so switching tabs never resizes the
 * modal; no adapter → the JSON view alone (the only content there is,
 * whatever the setting). `extraTabs` (feature tabs such as Remote commands,
 * `{ value, labelKey, Content, contentProps? }`) go after the adapter tabs and
 * before Raw JSON; empty by default, which changes nothing. Mount with
 * `key={record.id}` so the active tab resets per reading. Only the active
 * panel is mounted.
 */
export const RawDataBody = ({ record, showRawJson, extraTabs = [] }) => {
	const { messages } = useIntl();
	const narrow = useMediaQuery(NARROW_QUERY, { matchMedia: safeMatchMedia });
	const adapter = getRawDataAdapter(record.manufacturerName);
	const tabs = adapter === null ? [] : [...adapter.tabs, ...extraTabs];
	if (adapter !== null && showRawJson) {
		tabs.push({ value: RAW_JSON_TAB, labelKey: 'oemDataModal_tabs_json', Content: RawDataJsonView });
	}
	const [active, setActive] = useState(tabs[0]?.value);

	if (adapter === null) {
		return (
			<div className="oem-raw-data-panel">
				<RawDataJsonView record={record} />
			</div>
		);
	}

	const { Content, contentProps } = tabs.find((tab) => tab.value === active) || tabs[0];

	return (
		<div className="oem-raw-data-layout">
			<Tabs
				orientation={narrow ? 'horizontal' : 'vertical'}
				variant="scrollable"
				scrollButtons={false}
				value={active}
				onChange={(_, value) => setActive(value)}
				className="oem-raw-data-tabs"
			>
				{tabs.map(({ value, labelKey }) => (
					<Tab key={value} value={value} label={messages[labelKey]} className="oem-raw-data-tab" {...tabA11y(value)} />
				))}
			</Tabs>
			<div
				role="tabpanel"
				id={`oem-tabpanel-${active}`}
				aria-labelledby={`oem-tab-${active}`}
				className="oem-raw-data-panel"
			>
				<Content record={record} {...contentProps} />
			</div>
		</div>
	);
};
